import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  Chip,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  Tabs,
  Tab,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import {
  Edit as EditIcon,
  Description as DescriptionIcon,
  Assignment as AssignmentIcon,
  Email as EmailIcon,
  People as PeopleIcon,
  ArrowBack as ArrowBackIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Flag as FlagIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { projectService } from '../services/projectService';
import { Project, ProjectStatusLabels, ProjectPriorityLabels } from '../types/project';
import { dokumenteService, Dokument } from '../services/dokumenteService';
import { ticketService, Ticket } from '../services/ticketService';
import { emailService, Email } from '../services/emailService';
import { adresseService, Adresse } from '../services/adresseService';
import { benutzerService, Benutzer } from '../services/benutzerService';
import { milestoneService, Milestone, CreateMilestoneDto, UpdateMilestoneDto, MilestoneTicket } from '../services/milestoneService';

const ProjectDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [project, setProject] = useState<Project | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(0);

  // Assignment dialogs state
  const [documentDialogOpen, setDocumentDialogOpen] = useState(false);
  const [ticketDialogOpen, setTicketDialogOpen] = useState(false);
  const [mailDialogOpen, setMailDialogOpen] = useState(false);
  const [addressDialogOpen, setAddressDialogOpen] = useState(false);
  const [userDialogOpen, setUserDialogOpen] = useState(false);
  const [milestoneDialogOpen, setMilestoneDialogOpen] = useState(false);
  const [milestoneTicketDialogOpen, setMilestoneTicketDialogOpen] = useState(false);

  // Assigned items
  const [assignedDocuments, setAssignedDocuments] = useState<Dokument[]>([]);
  const [assignedTickets, setAssignedTickets] = useState<Ticket[]>([]);
  const [assignedMails, setAssignedMails] = useState<Email[]>([]);
  const [assignedAddresses, setAssignedAddresses] = useState<Adresse[]>([]);
  const [assignedUsers, setAssignedUsers] = useState<Benutzer[]>([]);
  const [milestones, setMilestones] = useState<Milestone[]>([]);
  const [selectedMilestone, setSelectedMilestone] = useState<Milestone | null>(null);
  const [milestoneTickets, setMilestoneTickets] = useState<MilestoneTicket[]>([]);
  const [editingMilestone, setEditingMilestone] = useState<Milestone | null>(null);

  // Available items for selection
  const [availableDocuments, setAvailableDocuments] = useState<Dokument[]>([]);
  const [availableTickets, setAvailableTickets] = useState<Ticket[]>([]);
  const [availableMails, setAvailableMails] = useState<Email[]>([]);
  const [availableAddresses, setAvailableAddresses] = useState<Adresse[]>([]);
  const [availableUsers, setAvailableUsers] = useState<Benutzer[]>([]);

  // Search state for dialogs
  const [documentSearch, setDocumentSearch] = useState('');
  const [ticketSearch, setTicketSearch] = useState('');
  const [mailSearch, setMailSearch] = useState('');
  const [addressSearch, setAddressSearch] = useState('');
  const [userSearch, setUserSearch] = useState('');

  // Milestone form state
  const [milestoneForm, setMilestoneForm] = useState({
    name: '',
    beschreibung: '',
    zielDatum: '',
  });

  useEffect(() => {
    if (id) {
      loadProject();
      loadAssignedItems();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Load available items when dialogs open
  useEffect(() => {
    if (documentDialogOpen) loadAvailableDocuments();
  }, [documentDialogOpen]);

  useEffect(() => {
    if (ticketDialogOpen) loadAvailableTickets();
  }, [ticketDialogOpen]);

  useEffect(() => {
    if (mailDialogOpen) loadAvailableMails();
  }, [mailDialogOpen]);

  useEffect(() => {
    if (addressDialogOpen) loadAvailableAddresses();
  }, [addressDialogOpen]);

  useEffect(() => {
    if (userDialogOpen) loadAvailableUsers();
  }, [userDialogOpen]);

  const loadProject = async () => {
    if (!id) return;

    try {
      setLoading(true);
      const data = await projectService.getById(parseInt(id));
      setProject(data);
    } catch (error) {
      console.error('Fehler beim Laden des Projekts:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadAssignedItems = async () => {
    if (!id) return;
    const projectId = parseInt(id);

    try {
      const [docs, tickets, mails, addresses, users, projectMilestones] = await Promise.all([
        dokumenteService.getByProject(projectId),
        ticketService.getByProject(projectId),
        emailService.getByProject(projectId),
        adresseService.getByProject(projectId),
        benutzerService.getByProject(projectId),
        milestoneService.getByProject(projectId),
      ]);

      setAssignedDocuments(docs);
      setAssignedTickets(tickets);
      setAssignedMails(mails);
      setAssignedAddresses(addresses);
      setAssignedUsers(users);
      setMilestones(projectMilestones);
    } catch (error) {
      console.error('Fehler beim Laden der zugeordneten Items:', error);
    }
  };

  const loadAvailableDocuments = async () => {
    const docs = await dokumenteService.getAll();
    setAvailableDocuments(docs);
  };

  const loadAvailableTickets = async () => {
    const tickets = await ticketService.getAll();
    setAvailableTickets(tickets);
  };

  const loadAvailableMails = async () => {
    const mails = await emailService.getAll();
    setAvailableMails(mails);
  };

  const loadAvailableAddresses = async () => {
    const addresses = await adresseService.getAll();
    setAvailableAddresses(addresses);
  };

  const loadAvailableUsers = async () => {
    const users = await benutzerService.getAll();
    setAvailableUsers(users);
  };

  const handleAssignDocument = async (dokumentId: number) => {
    if (!id) return;
    try {
      await projectService.assignDocument(parseInt(id), dokumentId);
      setDocumentDialogOpen(false);
      setDocumentSearch('');
      loadProject();
      loadAssignedItems();
      alert('Dokument wurde zugeordnet');
    } catch (error) {
      console.error('Fehler beim Zuordnen:', error);
      alert('Fehler beim Zuordnen des Dokuments');
    }
  };

  const handleRemoveDocument = async (dokumentId: number) => {
    if (!id || !window.confirm('Möchten Sie diese Zuordnung wirklich entfernen?')) return;
    try {
      await projectService.removeDocument(parseInt(id), dokumentId);
      loadProject();
      loadAssignedItems();
      alert('Zuordnung wurde entfernt');
    } catch (error) {
      console.error('Fehler beim Entfernen:', error);
      alert('Fehler beim Entfernen der Zuordnung');
    }
  };

  const handleAssignTicket = async (ticketId: number) => {
    if (!id) return;
    try {
      await projectService.assignTicket(parseInt(id), ticketId);
      setTicketDialogOpen(false);
      setTicketSearch('');
      loadProject();
      loadAssignedItems();
      alert('Ticket wurde zugeordnet');
    } catch (error) {
      console.error('Fehler beim Zuordnen:', error);
      alert('Fehler beim Zuordnen des Tickets');
    }
  };

  const handleRemoveTicket = async (ticketId: number) => {
    if (!id || !window.confirm('Möchten Sie diese Zuordnung wirklich entfernen?')) return;
    try {
      await projectService.removeTicket(parseInt(id), ticketId);
      loadProject();
      loadAssignedItems();
      alert('Zuordnung wurde entfernt');
    } catch (error) {
      console.error('Fehler beim Entfernen:', error);
      alert('Fehler beim Entfernen der Zuordnung');
    }
  };

  const handleAssignMail = async (emailId: number) => {
    if (!id) return;
    try {
      await projectService.assignMail(parseInt(id), emailId);
      setMailDialogOpen(false);
      setMailSearch('');
      loadProject();
      loadAssignedItems();
      alert('E-Mail wurde zugeordnet');
    } catch (error) {
      console.error('Fehler beim Zuordnen:', error);
      alert('Fehler beim Zuordnen der E-Mail');
    }
  };

  const handleRemoveMail = async (emailId: number) => {
    if (!id || !window.confirm('Möchten Sie diese Zuordnung wirklich entfernen?')) return;
    try {
      await projectService.removeMail(parseInt(id), emailId);
      loadProject();
      loadAssignedItems();
      alert('Zuordnung wurde entfernt');
    } catch (error) {
      console.error('Fehler beim Entfernen:', error);
      alert('Fehler beim Entfernen der Zuordnung');
    }
  };

  const handleAssignAddress = async (adresseId: number, rolle?: string) => {
    if (!id) return;
    try {
      await projectService.assignAddress(parseInt(id), adresseId, rolle);
      setAddressDialogOpen(false);
      setAddressSearch('');
      loadProject();
      loadAssignedItems();
      alert('Adresse wurde zugeordnet');
    } catch (error) {
      console.error('Fehler beim Zuordnen:', error);
      alert('Fehler beim Zuordnen der Adresse');
    }
  };

  const handleRemoveAddress = async (adresseId: number) => {
    if (!id || !window.confirm('Möchten Sie diese Zuordnung wirklich entfernen?')) return;
    try {
      await projectService.removeAddress(parseInt(id), adresseId);
      loadProject();
      loadAssignedItems();
      alert('Zuordnung wurde entfernt');
    } catch (error) {
      console.error('Fehler beim Entfernen:', error);
      alert('Fehler beim Entfernen der Zuordnung');
    }
  };

  const handleAssignUser = async (benutzerId: number, rolle?: string) => {
    if (!id) return;
    try {
      await projectService.assignUser(parseInt(id), benutzerId, rolle);
      setUserDialogOpen(false);
      setUserSearch('');
      loadProject();
      loadAssignedItems();
      alert('Benutzer wurde zugeordnet');
    } catch (error) {
      console.error('Fehler beim Zuordnen:', error);
      alert('Fehler beim Zuordnen des Benutzers');
    }
  };

  const handleRemoveUser = async (benutzerId: number) => {
    if (!id || !window.confirm('Möchten Sie diese Zuordnung wirklich entfernen?')) return;
    try {
      await projectService.removeUser(parseInt(id), benutzerId);
      loadProject();
      loadAssignedItems();
      alert('Zuordnung wurde entfernt');
    } catch (error) {
      console.error('Fehler beim Entfernen:', error);
      alert('Fehler beim Entfernen der Zuordnung');
    }
  };

  // Milestone handlers
  const handleCreateMilestone = async () => {
    if (!id || !milestoneForm.name.trim()) {
      alert('Bitte geben Sie mindestens einen Namen ein');
      return;
    }
    try {
      const data: CreateMilestoneDto = {
        projectID: parseInt(id),
        name: milestoneForm.name,
        beschreibung: milestoneForm.beschreibung || undefined,
        zielDatum: milestoneForm.zielDatum || undefined,
      };
      await milestoneService.create(data);
      setMilestoneDialogOpen(false);
      setMilestoneForm({ name: '', beschreibung: '', zielDatum: '' });
      loadAssignedItems();
      alert('Meilenstein wurde erstellt');
    } catch (error) {
      console.error('Fehler beim Erstellen:', error);
      alert('Fehler beim Erstellen des Meilensteins');
    }
  };

  const handleUpdateMilestone = async () => {
    if (!editingMilestone) return;
    try {
      const data: UpdateMilestoneDto = {
        name: milestoneForm.name,
        beschreibung: milestoneForm.beschreibung || undefined,
        zielDatum: milestoneForm.zielDatum || undefined,
      };
      await milestoneService.update(editingMilestone.milestoneID, data);
      setMilestoneDialogOpen(false);
      setEditingMilestone(null);
      setMilestoneForm({ name: '', beschreibung: '', zielDatum: '' });
      loadAssignedItems();
      alert('Meilenstein wurde aktualisiert');
    } catch (error) {
      console.error('Fehler beim Aktualisieren:', error);
      alert('Fehler beim Aktualisieren des Meilensteins');
    }
  };

  const handleDeleteMilestone = async (milestoneId: number) => {
    if (!window.confirm('Möchten Sie diesen Meilenstein wirklich löschen?')) return;
    try {
      await milestoneService.delete(milestoneId);
      loadAssignedItems();
      alert('Meilenstein wurde gelöscht');
    } catch (error) {
      console.error('Fehler beim Löschen:', error);
      alert('Fehler beim Löschen des Meilensteins');
    }
  };

  const handleOpenMilestoneDialog = (milestone?: Milestone) => {
    if (milestone) {
      setEditingMilestone(milestone);
      setMilestoneForm({
        name: milestone.name,
        beschreibung: milestone.beschreibung || '',
        zielDatum: milestone.zielDatum ? milestone.zielDatum.substring(0, 10) : '',
      });
    } else {
      setEditingMilestone(null);
      setMilestoneForm({ name: '', beschreibung: '', zielDatum: '' });
    }
    setMilestoneDialogOpen(true);
  };

  const loadMilestoneTickets = async (milestone: Milestone) => {
    try {
      const tickets = await milestoneService.getTickets(milestone.milestoneID);
      setMilestoneTickets(tickets);
      setSelectedMilestone(milestone);
    } catch (error) {
      console.error('Fehler beim Laden der Tickets:', error);
    }
  };

  const handleAssignTicketToMilestone = async (ticketId: number) => {
    if (!selectedMilestone) return;
    try {
      await milestoneService.assignTicket(selectedMilestone.milestoneID, ticketId);
      setMilestoneTicketDialogOpen(false);
      loadMilestoneTickets(selectedMilestone);
      loadAssignedItems();
      alert('Ticket wurde zugeordnet');
    } catch (error) {
      console.error('Fehler beim Zuordnen:', error);
      alert('Fehler beim Zuordnen des Tickets');
    }
  };

  const handleRemoveTicketFromMilestone = async (ticketId: number) => {
    if (!selectedMilestone || !window.confirm('Möchten Sie diese Zuordnung wirklich entfernen?')) return;
    try {
      await milestoneService.removeTicket(selectedMilestone.milestoneID, ticketId);
      loadMilestoneTickets(selectedMilestone);
      loadAssignedItems();
      alert('Zuordnung wurde entfernt');
    } catch (error) {
      console.error('Fehler beim Entfernen:', error);
      alert('Fehler beim Entfernen der Zuordnung');
    }
  };

  const handleCompleteMilestone = async (milestone: Milestone) => {
    try {
      const data: UpdateMilestoneDto = {
        status: 2, // Erledigt
      };
      await milestoneService.update(milestone.milestoneID, data);
      loadAssignedItems();
      alert('Meilenstein als erledigt markiert');
    } catch (error) {
      console.error('Fehler beim Aktualisieren:', error);
      alert('Fehler beim Aktualisieren des Meilensteins');
    }
  };

  // Filter functions
  const filteredDocuments = availableDocuments.filter(doc =>
    doc.betreff.toLowerCase().includes(documentSearch.toLowerCase()) ||
    doc.dokumentTyp.toLowerCase().includes(documentSearch.toLowerCase())
  );

  const filteredTickets = availableTickets.filter(ticket =>
    ticket.titel.toLowerCase().includes(ticketSearch.toLowerCase()) ||
    ticket.was.toLowerCase().includes(ticketSearch.toLowerCase())
  );

  const filteredMails = availableMails.filter(mail =>
    mail.betreffText.toLowerCase().includes(mailSearch.toLowerCase()) ||
    (mail.absenderEmail && mail.absenderEmail.toLowerCase().includes(mailSearch.toLowerCase()))
  );

  const filteredAddresses = availableAddresses.filter(addr =>
    addr.vorname.toLowerCase().includes(addressSearch.toLowerCase()) ||
    addr.nachname.toLowerCase().includes(addressSearch.toLowerCase()) ||
    (addr.firma && addr.firma.toLowerCase().includes(addressSearch.toLowerCase()))
  );

  const filteredUsers = availableUsers.filter(user =>
    user.benutzername.toLowerCase().includes(userSearch.toLowerCase()) ||
    user.email.toLowerCase().includes(userSearch.toLowerCase()) ||
    (user.vorname && user.vorname.toLowerCase().includes(userSearch.toLowerCase())) ||
    (user.nachname && user.nachname.toLowerCase().includes(userSearch.toLowerCase()))
  );

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('de-DE');
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('de-CH', { style: 'currency', currency: 'CHF' }).format(amount);
  };

  const getStatusColor = (status: number) => {
    const colors: Record<number, 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'> = {
      0: 'info',
      1: 'success',
      2: 'warning',
      3: 'default',
      4: 'error',
    };
    return colors[status] || 'default';
  };

  const getPriorityColor = (priority: number) => {
    const colors: Record<number, 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'> = {
      0: 'success',
      1: 'info',
      2: 'warning',
      3: 'error',
    };
    return colors[priority] || 'default';
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (!project) {
    return (
      <Box textAlign="center" py={12}>
        <Typography color="text.secondary" gutterBottom>
          Projekt nicht gefunden
        </Typography>
        <Button
          variant="contained"
          onClick={() => navigate('/projects')}
          sx={{ mt: 2 }}
        >
          Zurück zur Übersicht
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box mb={3}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/projects')}
          sx={{ mb: 2 }}
        >
          Zurück zur Übersicht
        </Button>

        <Box display="flex" justifyContent="space-between" alignItems="flex-start">
          <Box flex={1}>
            <Box display="flex" alignItems="center" gap={2} mb={2}>
              <Typography variant="h4" component="h1">
                {project.name}
              </Typography>
              <Chip
                label={ProjectStatusLabels[project.status]}
                color={getStatusColor(project.status)}
                size="small"
              />
              <Chip
                label={`Priorität: ${ProjectPriorityLabels[project.prioritaet]}`}
                color={getPriorityColor(project.prioritaet)}
                size="small"
              />
            </Box>
            {project.beschreibung && (
              <Typography color="text.secondary" paragraph>
                {project.beschreibung}
              </Typography>
            )}
          </Box>

          <Button
            variant="contained"
            startIcon={<EditIcon />}
            onClick={() => navigate(`/projects/${id}/edit`)}
          >
            Bearbeiten
          </Button>
        </Box>
      </Box>

      {/* Info Cards */}
      <Grid container spacing={2} mb={3}>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Zeitraum
              </Typography>
              <Typography variant="h6">
                {formatDate(project.beginDatum)}
              </Typography>
              {project.endDatum && (
                <Typography variant="body2">
                  bis {formatDate(project.endDatum)}
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {project.budget && (
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Budget
                </Typography>
                <Typography variant="h6">
                  {formatCurrency(project.budget)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        )}

        {project.istKosten !== undefined && (
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Ist-Kosten
                </Typography>
                <Typography variant="h6">
                  {formatCurrency(project.istKosten)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        )}

        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Mandant
              </Typography>
              <Typography variant="h6">
                {project.mandantName}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Statistics */}
      <Grid container spacing={2} mb={3}>
        <Grid item xs={6} md={3}>
          <Card
            sx={{
              cursor: 'pointer',
              '&:hover': { boxShadow: 4 },
              transition: 'box-shadow 0.3s',
            }}
            onClick={() => setActiveTab(1)}
          >
            <CardContent>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <DescriptionIcon color="primary" />
                <Typography variant="body2" color="text.secondary">
                  Dokumente
                </Typography>
              </Box>
              <Typography variant="h4" color="primary">
                {assignedDocuments.length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={6} md={3}>
          <Card
            sx={{
              cursor: 'pointer',
              '&:hover': { boxShadow: 4 },
              transition: 'box-shadow 0.3s',
            }}
            onClick={() => setActiveTab(2)}
          >
            <CardContent>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <AssignmentIcon color="success" />
                <Typography variant="body2" color="text.secondary">
                  Tickets
                </Typography>
              </Box>
              <Typography variant="h4" color="success.main">
                {assignedTickets.length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={6} md={3}>
          <Card
            sx={{
              cursor: 'pointer',
              '&:hover': { boxShadow: 4 },
              transition: 'box-shadow 0.3s',
            }}
            onClick={() => setActiveTab(3)}
          >
            <CardContent>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <EmailIcon color="secondary" />
                <Typography variant="body2" color="text.secondary">
                  E-Mails
                </Typography>
              </Box>
              <Typography variant="h4" color="secondary">
                {assignedMails.length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={6} md={3}>
          <Card
            sx={{
              cursor: 'pointer',
              '&:hover': { boxShadow: 4 },
              transition: 'box-shadow 0.3s',
            }}
            onClick={() => setActiveTab(5)}
          >
            <CardContent>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <PeopleIcon color="warning" />
                <Typography variant="body2" color="text.secondary">
                  Team-Mitglieder
                </Typography>
              </Box>
              <Typography variant="h4" color="warning.main">
                {assignedUsers.length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabs */}
      <Paper>
        <Tabs
          value={activeTab}
          onChange={(e, newValue) => setActiveTab(newValue)}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab label="Übersicht" />
          <Tab label="Dokumente" />
          <Tab label="Tickets" />
          <Tab label="E-Mails" />
          <Tab label="Adressen" />
          <Tab label="Team" />
          <Tab label="Meilensteine" />
        </Tabs>
        <Divider />

        <Box p={3}>
          {/* Overview Tab */}
          {activeTab === 0 && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Projekt-Informationen
              </Typography>
              <Grid container spacing={3} mt={1}>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary">
                    Status:
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {ProjectStatusLabels[project.status]}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary">
                    Priorität:
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {ProjectPriorityLabels[project.prioritaet]}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary">
                    Erstellt am:
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {formatDate(project.erstelltAm)}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary">
                    Geändert am:
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {formatDate(project.geaendertAm)}
                  </Typography>
                </Grid>
                {project.farbe && (
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="text.secondary">
                      Farbe:
                    </Typography>
                    <Box
                      sx={{
                        display: 'inline-block',
                        width: 32,
                        height: 32,
                        borderRadius: 1,
                        backgroundColor: project.farbe,
                        border: '1px solid',
                        borderColor: 'divider',
                        mt: 0.5,
                      }}
                    />
                  </Grid>
                )}
              </Grid>
            </Box>
          )}

          {/* Documents Tab */}
          {activeTab === 1 && (
            <Box>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Typography variant="h6">
                  Dokumente ({assignedDocuments.length})
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => setDocumentDialogOpen(true)}
                >
                  Dokument zuordnen
                </Button>
              </Box>
              {assignedDocuments.length === 0 ? (
                <Typography color="text.secondary">
                  Keine Dokumente zugeordnet
                </Typography>
              ) : (
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Betreff</TableCell>
                        <TableCell>Typ</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Erstellt am</TableCell>
                        <TableCell align="right">Aktionen</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {assignedDocuments.map((doc) => (
                        <TableRow key={doc.dokumentID}>
                          <TableCell>{doc.betreff}</TableCell>
                          <TableCell>{doc.dokumentTyp}</TableCell>
                          <TableCell>{doc.status}</TableCell>
                          <TableCell>{formatDate(doc.erstelltAm)}</TableCell>
                          <TableCell align="right">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleRemoveDocument(doc.dokumentID)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          )}

          {/* Tickets Tab */}
          {activeTab === 2 && (
            <Box>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Typography variant="h6">
                  Tickets ({assignedTickets.length})
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => setTicketDialogOpen(true)}
                >
                  Ticket zuordnen
                </Button>
              </Box>
              {assignedTickets.length === 0 ? (
                <Typography color="text.secondary">
                  Keine Tickets zugeordnet
                </Typography>
              ) : (
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Titel</TableCell>
                        <TableCell>Was</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Erstellt am</TableCell>
                        <TableCell align="right">Aktionen</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {assignedTickets.map((ticket) => (
                        <TableRow key={ticket.ticketID}>
                          <TableCell>{ticket.titel}</TableCell>
                          <TableCell>{ticket.was}</TableCell>
                          <TableCell>{ticket.status}</TableCell>
                          <TableCell>{formatDate(ticket.erstelltAm)}</TableCell>
                          <TableCell align="right">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleRemoveTicket(ticket.ticketID)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          )}

          {/* Emails Tab */}
          {activeTab === 3 && (
            <Box>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Typography variant="h6">
                  E-Mails ({assignedMails.length})
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => setMailDialogOpen(true)}
                >
                  E-Mail zuordnen
                </Button>
              </Box>
              {assignedMails.length === 0 ? (
                <Typography color="text.secondary">
                  Keine E-Mails zugeordnet
                </Typography>
              ) : (
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Betreff</TableCell>
                        <TableCell>Von</TableCell>
                        <TableCell>Empfangen am</TableCell>
                        <TableCell align="right">Aktionen</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {assignedMails.map((mail) => (
                        <TableRow key={mail.emailID}>
                          <TableCell>{mail.betreffText}</TableCell>
                          <TableCell>
                            {mail.absenderName || mail.absenderEmail || 'Unbekannt'}
                          </TableCell>
                          <TableCell>{formatDate(mail.empfangenAm)}</TableCell>
                          <TableCell align="right">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleRemoveMail(mail.emailID)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          )}

          {/* Addresses Tab */}
          {activeTab === 4 && (
            <Box>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Typography variant="h6">
                  Adressen ({assignedAddresses.length})
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => setAddressDialogOpen(true)}
                >
                  Adresse zuordnen
                </Button>
              </Box>
              {assignedAddresses.length === 0 ? (
                <Typography color="text.secondary">
                  Keine Adressen zugeordnet
                </Typography>
              ) : (
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Name</TableCell>
                        <TableCell>Firma</TableCell>
                        <TableCell>E-Mail</TableCell>
                        <TableCell>Rolle</TableCell>
                        <TableCell align="right">Aktionen</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {assignedAddresses.map((addr) => (
                        <TableRow key={addr.adresseID}>
                          <TableCell>{`${addr.vorname} ${addr.nachname}`}</TableCell>
                          <TableCell>{addr.firma || '-'}</TableCell>
                          <TableCell>{addr.email || '-'}</TableCell>
                          <TableCell>{addr.rolle || '-'}</TableCell>
                          <TableCell align="right">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleRemoveAddress(addr.adresseID)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          )}

          {/* Team Tab */}
          {activeTab === 5 && (
            <Box>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Typography variant="h6">
                  Team-Mitglieder ({assignedUsers.length})
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => setUserDialogOpen(true)}
                >
                  Mitglied hinzufügen
                </Button>
              </Box>
              {assignedUsers.length === 0 ? (
                <Typography color="text.secondary">
                  Keine Team-Mitglieder zugeordnet
                </Typography>
              ) : (
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Benutzername</TableCell>
                        <TableCell>Name</TableCell>
                        <TableCell>E-Mail</TableCell>
                        <TableCell>Rolle</TableCell>
                        <TableCell align="right">Aktionen</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {assignedUsers.map((user) => (
                        <TableRow key={user.benutzerID}>
                          <TableCell>{user.benutzername}</TableCell>
                          <TableCell>
                            {user.vorname || user.nachname
                              ? `${user.vorname || ''} ${user.nachname || ''}`.trim()
                              : '-'}
                          </TableCell>
                          <TableCell>{user.email}</TableCell>
                          <TableCell>{user.rolle || '-'}</TableCell>
                          <TableCell align="right">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleRemoveUser(user.benutzerID)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          )}

          {/* Milestones Tab */}
          {activeTab === 6 && (
            <Box>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Typography variant="h6">
                  Meilensteine ({milestones.length})
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => handleOpenMilestoneDialog()}
                >
                  Meilenstein erstellen
                </Button>
              </Box>
              {milestones.length === 0 ? (
                <Typography color="text.secondary">
                  Keine Meilensteine vorhanden
                </Typography>
              ) : (
                <Grid container spacing={2}>
                  {milestones.map((milestone) => {
                    const statusLabels = ['Offen', 'In Arbeit', 'Erledigt', 'Abgebrochen'];
                    const statusColors: Record<number, 'default' | 'primary' | 'success' | 'error'> = {
                      0: 'default',
                      1: 'primary',
                      2: 'success',
                      3: 'error',
                    };

                    return (
                      <Grid item xs={12} md={6} key={milestone.milestoneID}>
                        <Card>
                          <CardContent>
                            <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                              <Box flex={1}>
                                <Box display="flex" alignItems="center" gap={1} mb={1}>
                                  <FlagIcon color="primary" />
                                  <Typography variant="h6">
                                    {milestone.name}
                                  </Typography>
                                </Box>
                                {milestone.beschreibung && (
                                  <Typography variant="body2" color="text.secondary" mb={1}>
                                    {milestone.beschreibung}
                                  </Typography>
                                )}
                              </Box>
                              <Box>
                                <IconButton
                                  size="small"
                                  onClick={() => handleOpenMilestoneDialog(milestone)}
                                >
                                  <EditIcon />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => handleDeleteMilestone(milestone.milestoneID)}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </Box>
                            </Box>

                            <Grid container spacing={1} mb={2}>
                              <Grid item xs={6}>
                                <Typography variant="caption" color="text.secondary">
                                  Status:
                                </Typography>
                                <Box>
                                  <Chip
                                    size="small"
                                    label={statusLabels[milestone.status]}
                                    color={statusColors[milestone.status]}
                                  />
                                </Box>
                              </Grid>
                              <Grid item xs={6}>
                                <Typography variant="caption" color="text.secondary">
                                  Fortschritt:
                                </Typography>
                                <Typography variant="body2" fontWeight="medium">
                                  {milestone.fortschritt}%
                                </Typography>
                              </Grid>
                              {milestone.zielDatum && (
                                <Grid item xs={12}>
                                  <Typography variant="caption" color="text.secondary">
                                    Zieldatum:
                                  </Typography>
                                  <Typography variant="body2">
                                    {formatDate(milestone.zielDatum)}
                                  </Typography>
                                </Grid>
                              )}
                              <Grid item xs={12}>
                                <Typography variant="caption" color="text.secondary">
                                  Tickets:
                                </Typography>
                                <Typography variant="body2">
                                  {milestone.completedTicketCount || 0} / {milestone.ticketCount || 0} erledigt
                                </Typography>
                              </Grid>
                            </Grid>

                            <Box display="flex" gap={1}>
                              <Button
                                size="small"
                                variant="outlined"
                                startIcon={<AssignmentIcon />}
                                onClick={() => {
                                  loadMilestoneTickets(milestone);
                                  setMilestoneTicketDialogOpen(true);
                                }}
                              >
                                Tickets verwalten
                              </Button>
                              {milestone.status !== 2 && (
                                <Button
                                  size="small"
                                  variant="outlined"
                                  color="success"
                                  startIcon={<CheckCircleIcon />}
                                  onClick={() => handleCompleteMilestone(milestone)}
                                >
                                  Abschließen
                                </Button>
                              )}
                            </Box>
                          </CardContent>
                        </Card>
                      </Grid>
                    );
                  })}
                </Grid>
              )}
            </Box>
          )}
        </Box>
      </Paper>

      {/* Document Assignment Dialog */}
      <Dialog open={documentDialogOpen} onClose={() => setDocumentDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Dokument zuordnen</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            placeholder="Dokumente durchsuchen..."
            value={documentSearch}
            onChange={(e) => setDocumentSearch(e.target.value)}
            sx={{ mb: 2, mt: 1 }}
          />
          {filteredDocuments.length === 0 ? (
            <Typography color="text.secondary">
              {availableDocuments.length === 0
                ? 'Keine Dokumente verfügbar'
                : 'Keine Dokumente gefunden'}
            </Typography>
          ) : (
            <TableContainer sx={{ maxHeight: 400 }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Betreff</TableCell>
                    <TableCell>Typ</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Aktion</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredDocuments.map((doc) => (
                    <TableRow key={doc.dokumentID}>
                      <TableCell>{doc.betreff}</TableCell>
                      <TableCell>{doc.dokumentTyp}</TableCell>
                      <TableCell>{doc.status}</TableCell>
                      <TableCell align="right">
                        <Button
                          size="small"
                          variant="contained"
                          onClick={() => handleAssignDocument(doc.dokumentID)}
                          disabled={assignedDocuments.some(d => d.dokumentID === doc.dokumentID)}
                        >
                          {assignedDocuments.some(d => d.dokumentID === doc.dokumentID) ? 'Zugeordnet' : 'Zuordnen'}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDocumentDialogOpen(false)}>Schließen</Button>
        </DialogActions>
      </Dialog>

      {/* Ticket Assignment Dialog */}
      <Dialog open={ticketDialogOpen} onClose={() => setTicketDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Ticket zuordnen</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            placeholder="Tickets durchsuchen..."
            value={ticketSearch}
            onChange={(e) => setTicketSearch(e.target.value)}
            sx={{ mb: 2, mt: 1 }}
          />
          {filteredTickets.length === 0 ? (
            <Typography color="text.secondary">
              {availableTickets.length === 0
                ? 'Keine Tickets verfügbar'
                : 'Keine Tickets gefunden'}
            </Typography>
          ) : (
            <TableContainer sx={{ maxHeight: 400 }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Titel</TableCell>
                    <TableCell>Was</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Aktion</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredTickets.map((ticket) => (
                    <TableRow key={ticket.ticketID}>
                      <TableCell>{ticket.titel}</TableCell>
                      <TableCell>{ticket.was.substring(0, 50)}...</TableCell>
                      <TableCell>{ticket.status}</TableCell>
                      <TableCell align="right">
                        <Button
                          size="small"
                          variant="contained"
                          onClick={() => handleAssignTicket(ticket.ticketID)}
                          disabled={assignedTickets.some(t => t.ticketID === ticket.ticketID)}
                        >
                          {assignedTickets.some(t => t.ticketID === ticket.ticketID) ? 'Zugeordnet' : 'Zuordnen'}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTicketDialogOpen(false)}>Schließen</Button>
        </DialogActions>
      </Dialog>

      {/* Email Assignment Dialog */}
      <Dialog open={mailDialogOpen} onClose={() => setMailDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>E-Mail zuordnen</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            placeholder="E-Mails durchsuchen..."
            value={mailSearch}
            onChange={(e) => setMailSearch(e.target.value)}
            sx={{ mb: 2, mt: 1 }}
          />
          {filteredMails.length === 0 ? (
            <Typography color="text.secondary">
              {availableMails.length === 0
                ? 'Keine E-Mails verfügbar'
                : 'Keine E-Mails gefunden'}
            </Typography>
          ) : (
            <TableContainer sx={{ maxHeight: 400 }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Betreff</TableCell>
                    <TableCell>Von</TableCell>
                    <TableCell>Empfangen</TableCell>
                    <TableCell align="right">Aktion</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredMails.map((mail) => (
                    <TableRow key={mail.emailID}>
                      <TableCell>{mail.betreffText}</TableCell>
                      <TableCell>{mail.absenderName || mail.absenderEmail}</TableCell>
                      <TableCell>{formatDate(mail.empfangenAm)}</TableCell>
                      <TableCell align="right">
                        <Button
                          size="small"
                          variant="contained"
                          onClick={() => handleAssignMail(mail.emailID)}
                          disabled={assignedMails.some(m => m.emailID === mail.emailID)}
                        >
                          {assignedMails.some(m => m.emailID === mail.emailID) ? 'Zugeordnet' : 'Zuordnen'}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMailDialogOpen(false)}>Schließen</Button>
        </DialogActions>
      </Dialog>

      {/* Address Assignment Dialog */}
      <Dialog open={addressDialogOpen} onClose={() => setAddressDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Adresse zuordnen</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            placeholder="Adressen durchsuchen..."
            value={addressSearch}
            onChange={(e) => setAddressSearch(e.target.value)}
            sx={{ mb: 2, mt: 1 }}
          />
          {filteredAddresses.length === 0 ? (
            <Typography color="text.secondary">
              {availableAddresses.length === 0
                ? 'Keine Adressen verfügbar'
                : 'Keine Adressen gefunden'}
            </Typography>
          ) : (
            <TableContainer sx={{ maxHeight: 400 }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Firma</TableCell>
                    <TableCell>E-Mail</TableCell>
                    <TableCell align="right">Aktion</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredAddresses.map((addr) => (
                    <TableRow key={addr.adresseID}>
                      <TableCell>{`${addr.vorname} ${addr.nachname}`}</TableCell>
                      <TableCell>{addr.firma || '-'}</TableCell>
                      <TableCell>{addr.email || '-'}</TableCell>
                      <TableCell align="right">
                        <Button
                          size="small"
                          variant="contained"
                          onClick={() => handleAssignAddress(addr.adresseID)}
                          disabled={assignedAddresses.some(a => a.adresseID === addr.adresseID)}
                        >
                          {assignedAddresses.some(a => a.adresseID === addr.adresseID) ? 'Zugeordnet' : 'Zuordnen'}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddressDialogOpen(false)}>Schließen</Button>
        </DialogActions>
      </Dialog>

      {/* User Assignment Dialog */}
      <Dialog open={userDialogOpen} onClose={() => setUserDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Team-Mitglied hinzufügen</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            placeholder="Benutzer durchsuchen..."
            value={userSearch}
            onChange={(e) => setUserSearch(e.target.value)}
            sx={{ mb: 2, mt: 1 }}
          />
          {filteredUsers.length === 0 ? (
            <Typography color="text.secondary">
              {availableUsers.length === 0
                ? 'Keine Benutzer verfügbar'
                : 'Keine Benutzer gefunden'}
            </Typography>
          ) : (
            <TableContainer sx={{ maxHeight: 400 }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Benutzername</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell>E-Mail</TableCell>
                    <TableCell align="right">Aktion</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredUsers.map((user) => (
                    <TableRow key={user.benutzerID}>
                      <TableCell>{user.benutzername}</TableCell>
                      <TableCell>
                        {user.vorname || user.nachname
                          ? `${user.vorname || ''} ${user.nachname || ''}`.trim()
                          : '-'}
                      </TableCell>
                      <TableCell>{user.email}</TableCell>
                      <TableCell align="right">
                        <Button
                          size="small"
                          variant="contained"
                          onClick={() => handleAssignUser(user.benutzerID)}
                          disabled={assignedUsers.some(u => u.benutzerID === user.benutzerID)}
                        >
                          {assignedUsers.some(u => u.benutzerID === user.benutzerID) ? 'Zugeordnet' : 'Zuordnen'}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUserDialogOpen(false)}>Schließen</Button>
        </DialogActions>
      </Dialog>

      {/* Milestone Create/Edit Dialog */}
      <Dialog open={milestoneDialogOpen} onClose={() => setMilestoneDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingMilestone ? 'Meilenstein bearbeiten' : 'Neuen Meilenstein erstellen'}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Name"
            value={milestoneForm.name}
            onChange={(e) => setMilestoneForm({ ...milestoneForm, name: e.target.value })}
            sx={{ mb: 2, mt: 1 }}
            required
          />
          <TextField
            fullWidth
            label="Beschreibung"
            value={milestoneForm.beschreibung}
            onChange={(e) => setMilestoneForm({ ...milestoneForm, beschreibung: e.target.value })}
            multiline
            rows={3}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label="Zieldatum"
            type="date"
            value={milestoneForm.zielDatum}
            onChange={(e) => setMilestoneForm({ ...milestoneForm, zielDatum: e.target.value })}
            InputLabelProps={{ shrink: true }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMilestoneDialogOpen(false)}>Abbrechen</Button>
          <Button
            variant="contained"
            onClick={editingMilestone ? handleUpdateMilestone : handleCreateMilestone}
          >
            {editingMilestone ? 'Aktualisieren' : 'Erstellen'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Milestone Ticket Management Dialog */}
      <Dialog open={milestoneTicketDialogOpen} onClose={() => setMilestoneTicketDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {selectedMilestone ? `Tickets für "${selectedMilestone.name}"` : 'Tickets verwalten'}
        </DialogTitle>
        <DialogContent>
          {selectedMilestone && (
            <Box>
              <Box mb={3}>
                <Typography variant="subtitle2" gutterBottom>
                  Zugeordnete Tickets ({milestoneTickets.length})
                </Typography>
                {milestoneTickets.length === 0 ? (
                  <Typography color="text.secondary">Keine Tickets zugeordnet</Typography>
                ) : (
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Titel</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell align="right">Aktion</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {milestoneTickets.map((ticket) => (
                          <TableRow key={ticket.ticketID}>
                            <TableCell>{ticket.titel}</TableCell>
                            <TableCell>{ticket.status}</TableCell>
                            <TableCell align="right">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleRemoveTicketFromMilestone(ticket.ticketID)}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Box>

              <Divider sx={{ my: 2 }} />

              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Ticket hinzufügen
                </Typography>
                <Typography variant="body2" color="text.secondary" mb={2}>
                  Tickets aus dem Projekt, die noch keinem Meilenstein zugeordnet sind:
                </Typography>
                {assignedTickets.filter(t => !milestoneTickets.some(mt => mt.ticketID === t.ticketID)).length === 0 ? (
                  <Typography color="text.secondary">Keine verfügbaren Tickets</Typography>
                ) : (
                  <TableContainer sx={{ maxHeight: 300 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Titel</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell align="right">Aktion</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {assignedTickets
                          .filter(t => !milestoneTickets.some(mt => mt.ticketID === t.ticketID))
                          .map((ticket) => (
                            <TableRow key={ticket.ticketID}>
                              <TableCell>{ticket.titel}</TableCell>
                              <TableCell>{ticket.status}</TableCell>
                              <TableCell align="right">
                                <Button
                                  size="small"
                                  variant="contained"
                                  onClick={() => handleAssignTicketToMilestone(ticket.ticketID)}
                                >
                                  Hinzufügen
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMilestoneTicketDialogOpen(false)}>Schließen</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProjectDetail;
